import '@/Content/Webpack/themes/default-v2/tailwind.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '@fortawesome/fontawesome-free/js/all.min.js'
import { initAccordions, watchForNewAccordions } from '@shared/utils/accordion';
import { Sheet } from '@shared/utils/sheet';
import { initSlotCountdown } from './slotCountdown';

declare global {
  interface Window {
    /**
     * Opens the order-options flow: asks the server whether to redirect directly or to show the
     * click & collect / delivery choice popup. Used by the navbar buttons and the add-to-cart flow.
     */
    openOrderOptions?: (fallbackHref?: string, itemId?: string) => Promise<void>;
  }
}

/**
 * Initializes navigation elements and event listeners for mobile menu and dropdown menus.
 */
function initializeNavigation() {
  const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
  const mobileMenu = document.getElementById('mobile-menu');
  const iconHamburger = document.getElementById('icon-hamburger');
  const iconClose = document.getElementById('icon-close');

  // Toggle mobile menu with a smooth fade + slide-down transition. We keep `hidden` for the final
  // collapsed state (so the menu is removed from the a11y tree and DOM flow) and animate opacity +
  // transform on top of it, mirroring the open/close approach of the shared Sheet utility.
  if (mobileMenuToggle && mobileMenu) {
    const menu = mobileMenu;
    const toggleBtn = mobileMenuToggle;
    // Transition duration in ms, must match the `duration-300` utility on the menu element.
    const TRANSITION_MS = 300;
    let closeTimer: number | undefined;

    const openMenu = () => {
      // Abort a pending close so a rapid re-open never leaves the menu half-hidden.
      window.clearTimeout(closeTimer);
      menu.classList.remove('hidden');
      menu.classList.add('flex');

      // Let the browser paint the collapsed start state before flipping to the visible one,
      // otherwise the transition is skipped (double rAF, same as Sheet.open()).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          menu.classList.remove('opacity-0', '-translate-y-3');
        });
      });

      iconHamburger?.classList.add('hidden');
      iconClose?.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');

      // Keep assistive tech in sync with the visible state (WCAG 4.1.2)
      toggleBtn.setAttribute('aria-expanded', 'true');
      toggleBtn.setAttribute('aria-label', 'Menü schließen');
    };

    const closeMenu = () => {
      menu.classList.add('opacity-0', '-translate-y-3');

      // Remove from flow only after the fade-out has finished.
      closeTimer = window.setTimeout(() => {
        menu.classList.remove('flex');
        menu.classList.add('hidden');
      }, TRANSITION_MS);

      iconHamburger?.classList.remove('hidden');
      iconClose?.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');

      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.setAttribute('aria-label', 'Menü öffnen');
    };

    mobileMenuToggle.addEventListener('click', () => {
      const isOpen = toggleBtn.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });
  }

  // Initialize all mobile dropdown menus (supports multiple dynamic dropdowns)
  const dropdownContainers = document.querySelectorAll('.mobile-dropdown-container');
  
  dropdownContainers.forEach((container) => {
    const toggle = container.querySelector('.mobile-dropdown-toggle');
    const list = container.querySelector('.mobile-dropdown-list');
    const arrow = container.querySelector('.mobile-dropdown-arrow');

    if (toggle && list && arrow) {
      toggle.addEventListener('click', () => {
        list.classList.toggle('hidden');
        arrow.classList.toggle('rotate-180');
        toggle.classList.toggle('bg-company');
        toggle.classList.toggle('text-company-contrast');
      });
    }
  });
}

/**
 * Wires up the order-options popup used by the DefaultV2 "Online Bestellen" navbar buttons (and,
 * via the exposed window.openOrderOptions, by the add-to-cart flow). The server decides (via the
 * OrderOptions endpoint) whether to redirect straight away (single mode / existing cart) or to let
 * the user choose between click & collect and delivery. A fallback URL keeps things working when
 * the request fails or the popup markup is missing.
 */
function initOrderOptions() {
  const modal = document.getElementById('order-options-modal');
  const optionsUrl = modal?.dataset.orderOptionsUrl;

  // Reuse the shared Sheet utility for the open/close animation (mobile slide-up, desktop scale/fade),
  // consistent with the item-options / item-combi popups. Sheet wires the overlay-click to close itself.
  const sheet = modal ? new Sheet('order-options-modal') : null;

  const openModal = () => sheet?.open();
  const closeModal = () => sheet?.close();

  /**
   * Asks the server how to continue and either redirects or opens the choice popup.
   * @param fallbackHref Where to navigate if the request fails or no popup can be shown.
   * @param itemId Optional item to forward, so the user returns to it after picking a mode.
   */
  const openOrderOptions = async (fallbackHref?: string, itemId?: string) => {
    if (!optionsUrl) {
      // Without the endpoint URL we cannot ask the server: just follow the fallback.
      if (fallbackHref) window.location.href = fallbackHref;
      return;
    }
    const requestUrl = itemId
      ? `${optionsUrl}?itemId=${encodeURIComponent(itemId)}`
      : optionsUrl;
    try {
      const response = await fetch(requestUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const data = await response.json();

      if (data.mode === 'redirect' && data.url) {
        window.location.href = data.url;
        return;
      }

      if (data.mode === 'choose' && modal) {
        // Apply the resolved follow-up URLs and open the popup.
        if (data.clickCollectUrl) {
          const clcLink = document.getElementById('order-option-clc') as HTMLAnchorElement | null;
          if (clcLink) clcLink.href = data.clickCollectUrl;
        }
        if (data.deliveryUrl) {
          const deliveryLink = document.getElementById('order-option-delivery') as HTMLAnchorElement | null;
          if (deliveryLink) deliveryLink.href = data.deliveryUrl;
        }
        openModal();
        return;
      }

      // No popup available for a "choose" result: fall back to the full page.
      if (fallbackHref) window.location.href = fallbackHref;
    } catch {
      if (fallbackHref) window.location.href = fallbackHref;
    }
  };

  // Expose for non-navbar callers (e.g. the add-to-cart flow when no store is selected yet).
  window.openOrderOptions = openOrderOptions;

  document.querySelectorAll<HTMLAnchorElement>('.js-order-options-trigger').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      void openOrderOptions(trigger.href);
    });
  });

  // Overlay click is wired by Sheet itself; only the explicit close button needs handling here.
  document.getElementById('order-options-close')?.addEventListener('click', closeModal);
}

/**
 * Scrolls to the element referenced by the current URL hash (e.g. #vorteile, #faq).
 * Deferred so lazily-populated sections (product/news grids) have rendered and the
 * target offset is correct when navigating to an anchor from another page.
 */
function scrollToHashTarget() {
  const hash = window.location.hash;
  if (hash.length < 2) return;

  const target = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (target) {
    window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  }
}

// NOTE: actually you dont need to wait for document ready cuz scripts are loaded via defer
// Use native DOM ready check (faster than jQuery)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeNavigation();
    initOrderOptions();
    initAccordions();
    watchForNewAccordions();
    scrollToHashTarget();
    initSlotCountdown();
  });
} else {
  initializeNavigation();
  initOrderOptions();
  initAccordions();
  watchForNewAccordions();
  scrollToHashTarget();
  initSlotCountdown();
}

window.addEventListener('hashchange', scrollToHashTarget);

export {};

